import { Button, Loader } from "@caprikorn/dynamo-ui";
import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { ArrowLeftMd, TrashEmpty } from "@icons";
import { ROUTES } from "@router/router.constants";
import type { UserDetailsParams } from "@router/router.types";
import { useGetCustomersAllByGroup } from "@services/apiEcms/useGetCustomersAllByGroup";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useGetUser } from "@services/apiEcms/useGetUser";
import { usePatchUserAdminStatus } from "@services/apiEcms/usePatchUserAdminStatus";
import { usePatchUserAuthorizations } from "@services/apiEcms/usePatchUserAuthorizations";
import { formatCaughtError } from "@utils/error.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./UserDetails.module.css";
import { AccessErrors } from "../AccessErrors/AccessErrors";
import { AuthorizationsDrawer } from "../AuthorizationsDrawer/AuthorizationsDrawer";
import { DeleteUserModal } from "../DeleteUserModal/DeleteUserModal";
import { IdentityDrawer } from "../IdentityDrawer/IdentityDrawer";
import { RoleDrawer } from "../RoleDrawer/RoleDrawer";
import type { AccessRole } from "../RoleDrawer/RoleDrawer.types";
import { UserAuthorizationsCard } from "../UserAuthorizationsCard/UserAuthorizationsCard";
import { UserInformationsCard } from "../UserInformationsCard/UserInformationsCard";
import { UserRoleCard } from "../UserRoleCard/UserRoleCard";

export const UserDetails: FC = () => {
  const { t } = useCustomTranslation(["main", "common"]);
  const navigate = useNavigate();
  const { currentGroup } = useGroupReferences();
  const { userId = "" } = useParams<UserDetailsParams>();

  const [showAuthorizationsDrawer, setShowAuthorizationsDrawer] =
    useState(false);
  const [showUserDrawer, setShowUserDrawer] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRoleDrawer, setShowRoleDrawer] = useState(false);

  const { data: isGroupAdmin, isLoading: isGroupAdminLoading } = useGetGroup({
    groupId: currentGroup,
    options: { select: (res) => res.isAdmin },
  });

  const {
    data: userData,
    refetch: userRefetch,
    error: userError,
    isFetching: isUserFetching,
    isLoading: isUserLoading,
  } = useGetUser(userId, isGroupAdmin);

  const {
    mutate: editAuthorizationsMutate,
    isError: isAuthorizationsEditError,
    isPending: isAuthorizationsEditLoading,
    error: authorizationsError,
    reset: resetAuthorizations,
  } = usePatchUserAuthorizations();

  const { mutate: updateUserRole, isPending: isUserRoleUpdating } =
    usePatchUserAdminStatus();

  const openAuthorizationsDrawer = () => {
    setShowAuthorizationsDrawer(true);
    if (authorizationsError) {
      resetAuthorizations();
    }
  };

  const {
    data: customersByGroupData,
    isError: isCustomerByGroupError,
    refetch: refetchCustomerByGroup,
  } = useGetCustomersAllByGroup();

  const onSubmitRole = (role: AccessRole) =>
    updateUserRole(
      {
        isAdmin: role === "ADMIN",
        id: userId,
      },
      {
        onSuccess: () => {
          setShowRoleDrawer(false);
        },
      },
    );

  const onSubmitAuthorizations = (authorizations: string[]) => {
    editAuthorizationsMutate(
      { id: userId, authorizations },
      {
        onSuccess: () => {
          setShowAuthorizationsDrawer(false);
        },
      },
    );
  };

  if (isUserLoading || isGroupAdminLoading) {
    return <Loader size="lg" />;
  }

  if (!isGroupAdmin || userError || !userData) {
    return (
      <div className={classes.accessErrors}>
        <AccessErrors
          errorStatus={
            !isGroupAdmin ? 403 : formatCaughtError(userError).status
          }
          location="details"
          refetch={userRefetch}
        />
      </div>
    );
  }

  const role = userData.isAdmin ? "admin" : "restricted";
  const customerIdsCount =
    userData.authorizations?.length ?? customersByGroupData?.length ?? 0;

  return (
    <div className={classes.container}>
      <UserInformationsCard
        createdAt={userData.createdAt || ""}
        email={userData.email}
        firstName={userData.firstName}
        lastName={userData.lastName}
        onClick={() => setShowUserDrawer(true)}
        updatedAt={userData.updatedAt || ""}
      />
      <UserRoleCard onClick={() => setShowRoleDrawer(true)} role={role} />
      <UserAuthorizationsCard
        customerIdsCount={customerIdsCount}
        isAdmin={userData.isAdmin}
        isLoading={isAuthorizationsEditLoading || isUserFetching}
        onClick={openAuthorizationsDrawer}
      />
      <div className={classes.buttons}>
        <Button
          as={Link}
          startIcon={ArrowLeftMd}
          to={ROUTES.ACCESS_MANAGER}
          variant="tertiary"
        >
          {t("button.back", { ns: "common" })}
        </Button>
        <Button
          onClick={() => setShowModal(true)}
          startIcon={TrashEmpty}
          variant="secondary"
        >
          {t("userDetails.deleteUser")}
        </Button>
      </div>
      {showModal && (
        <DeleteUserModal
          onClose={() => setShowModal(false)}
          onDelete={() => {
            navigate(ROUTES.ACCESS_MANAGER);
          }}
          user={userData}
        />
      )}
      {showAuthorizationsDrawer && (
        <AuthorizationsDrawer
          currentAuthorizations={userData.authorizations ?? []}
          customerList={customersByGroupData}
          errorStatus={
            authorizationsError
              ? formatCaughtError(authorizationsError).status
              : undefined
          }
          isError={isAuthorizationsEditError || isCustomerByGroupError}
          isSubmitError={isAuthorizationsEditError}
          isSubmitting={isAuthorizationsEditLoading}
          onHide={() => setShowAuthorizationsDrawer(false)}
          onSubmit={onSubmitAuthorizations}
          refetch={refetchCustomerByGroup}
        />
      )}
      {showUserDrawer && (
        <IdentityDrawer
          onClose={() => setShowUserDrawer(false)}
          userId={userId}
          userIdentityData={userData}
        />
      )}
      {showRoleDrawer && (
        <RoleDrawer
          isAdmin={userData.isAdmin}
          isSubmitting={isUserRoleUpdating}
          onHide={() => setShowRoleDrawer(false)}
          onSubmit={onSubmitRole}
        />
      )}
    </div>
  );
};
